import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  ExportOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
  GlobalOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { servicesService } from '../../services';
import { ServiceEvent, ServiceEventResponse } from '../../services/services.service';
import { ConfirmationModal, ServiceEventsList, DetailPageHeader, HeaderActionButton, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { useNotificationContext } from '../../contexts/NotificationContext';
import MonitoringTab from '../ServiceDetail/components/MonitoringTab';
import LazyServiceEventsList from '../../components/shared/LazyServiceEventsList';

interface SiteService {
  id: string;
  name: string;
  type: string;
  status: string;
  blueprintId?: string;
  bundleId?: string;
  staticIp?: string;
  publicIp?: string;
  domainName?: string;
  endpointUrl?: string;
  region?: string;
  createdAt?: string;
  updatedAt?: string;
  metadata?: {
    siteConfig?: {
      name?: string;
      blueprintId?: string;
      bundleId?: string;
      environment?: string;
      description?: string;
      customDomain?: string;
    };
  };
  product?: {
    name?: string;
    description?: string;
    cpuCount?: number;
    ramSizeInGb?: number;
    diskSizeInGb?: number;
    transferPerMonthInGb?: number;
    price?: number;
  };
}

const mapLevelToEventType = (level?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!level) return 'info';
  const levelUpper = level.toUpperCase();
  switch (levelUpper) {
    case 'SUCCESS': return 'success';
    case 'ERROR': return 'error';
    case 'WARNING': return 'warning';
    default: return 'info';
  }
};

const mapStackStatusToEventType = (stackStatus?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!stackStatus) return 'info';
  const status = stackStatus.toUpperCase();
  if (status.includes('COMPLETE') && !status.includes('FAILED')) return 'success';
  if (status.includes('FAILED') || status.includes('ERROR') || status.includes('ROLLBACK')) return 'error';
  return 'info';
};

const mapServiceEventsToUI = (events: ServiceEventResponse[]): ServiceEvent[] => {
  return events.map((event, index) => {
    let timestamp: Date;
    try {
      timestamp = event.createdAt ? new Date(event.createdAt) : new Date();
      if (isNaN(timestamp.getTime())) timestamp = new Date();
    } catch {
      timestamp = new Date();
    }

    const eventType = event.level
      ? mapLevelToEventType(event.level)
      : mapStackStatusToEventType(event.stackStatus);

    const message = event.message || 'No message';

    const isKubiyEvent = event.source?.toLowerCase() === 'kubiy';
    const messageLower = message.toLowerCase();
    const isDeletionEvent = isKubiyEvent && (messageLower.includes('deleted') || messageLower.includes('eliminado'));

    let details: string | undefined = event.description || undefined;

    return {
      id: event.SK || `event-${index}-${event.createdAt}`,
      timestamp,
      type: eventType,
      message,
      details,
      source: event.source,
      isKubiyEvent,
      isDeletionEvent,
    };
  });
};

const mapStatus = (status: string): 'active' | 'inactive' | 'deploying' | 'error' => {
  switch (status?.toLowerCase()) {
    case 'running': return 'active';
    case 'stopped': return 'inactive';
    case 'creating':
    case 'initializing':
    case 'in_progress':
    case 'deploying':
    case 'stacking': return 'deploying';
    case 'error':
    case 'error_create_stack':
    case 'failed': return 'error';
    default: return 'inactive';
  }
};

const isServiceCreating = (status: string | undefined): boolean => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'deploying' || s === 'initializing' || s === 'stacking' || s === 'creating';
};

const SiteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();

  const [site, setSite] = useState<SiteService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [isRefreshingEvents, setIsRefreshingEvents] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'events' | 'monitoring'>('events');
  const hasFetchedRef = useRef<string | null>(null);

  const siteUrl = site?.endpointUrl || (site?.staticIp ? `http://${site.staticIp}` : (site?.publicIp ? `http://${site.publicIp}` : undefined));
  const siteConfig = site?.metadata?.siteConfig;
  const siteStatus = mapStatus(site?.status || '');

  const fetchEvents = useCallback(async () => {
    if (!id) return;
    try {
      setIsRefreshingEvents(true);
      const eventsData = await servicesService.getEvents(id);
      const mapped = mapServiceEventsToUI(eventsData);
      setEvents(mapped.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setIsRefreshingEvents(false);
    }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('No site ID provided');
        setLoading(false);
        return;
      }

      if (hasFetchedRef.current === id) return;
      hasFetchedRef.current = id;

      try {
        setLoading(true);
        setError(null);

        const serviceData: any = await servicesService.getById(id);

        setSite(serviceData);

      } catch (err: any) {
        console.error('Error fetching site:', err);
        setError(err.message || 'Error loading site details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Auto-refresh events while creating
  useEffect(() => {
    if (!site || !isServiceCreating(site.status)) return;

    const interval = setInterval(async () => {
      if (!id) return;
      try {
        const serviceData: any = await servicesService.getById(id);
        setSite(serviceData);

        const eventsData = await servicesService.getEvents(id);
        const mapped = mapServiceEventsToUI(eventsData);
        setEvents(mapped.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
      } catch (err) {
        console.error('Error refreshing:', err);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [site?.status, id]);

  const handleRefresh = async () => {
    if (!id) return;
    try {
      setLoading(true);
      hasFetchedRef.current = null;
      const serviceData: any = await servicesService.getById(id);
      setSite(serviceData);
      await fetchEvents();
    } catch (err: any) {
      showError(`Error refreshing site: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!id) return;
    try {
      setIsProcessing(true);
      await servicesService.start(id);
      success('Site is being started');
      handleRefresh();
    } catch (err: any) {
      showError(`Error starting site: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStop = async () => {
    if (!id) return;
    try {
      setIsProcessing(true);
      await servicesService.stop(id);
      success('Site has been stopped');
      setShowStopModal(false);
      handleRefresh();
    } catch (err: any) {
      showError(`Error stopping site: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestart = async () => {
    if (!id) return;
    try {
      setIsProcessing(true);
      await servicesService.restart(id);
      success('Site is being restarted');
      setShowActionsMenu(false);
      handleRefresh();
    } catch (err: any) {
      showError(`Error restarting site: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await servicesService.delete(id);
      success('Site has been deleted successfully');
      setShowDeleteModal(false);
      navigate('/sites');
    } catch (err: any) {
      showError(`Error deleting site: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    success('Copied to clipboard');
  };

  // Loading state
  if (loading && !site) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="flex items-center justify-center py-20">
          <LoadingOutlined className="text-3xl text-blue-500 mr-3" />
          <span className="text-gray-500 dark:text-gray-400">Loading site details...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="text-center py-20">
          <CloseCircleOutlined className="text-4xl text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/sites')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Back to Sites
          </button>
        </div>
      </div>
    );
  }

  if (!site) return null;

  // Creating state
  if (isServiceCreating(site.status)) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/sites')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeftOutlined />
            <span className="text-sm font-medium">Back to Sites</span>
          </button>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center mb-6">
          <LoadingOutlined className="text-4xl text-blue-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Creating {site.name}...</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Your site is being provisioned. This may take a few minutes.
          </p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
          >
            <ReloadOutlined />
            Refresh
          </button>
        </div>
        {events.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Events</h3>
            </div>
            <div className="p-6">
              <ServiceEventsList events={events} />
            </div>
          </div>
        )}
      </div>
    );
  }

  const statusConfig = {
    active: { color: 'bg-green-500', text: 'Active', textColor: 'text-green-700 dark:text-green-400' },
    inactive: { color: 'bg-gray-500', text: 'Inactive', textColor: 'text-gray-700 dark:text-gray-400' },
    deploying: { color: 'bg-blue-500', text: 'Deploying', textColor: 'text-blue-700 dark:text-blue-400' },
    error: { color: 'bg-red-500', text: 'Error', textColor: 'text-red-700 dark:text-red-400' },
  };

  const currentStatusConfig = statusConfig[siteStatus];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel="Back"
        onBack={() => navigate('/sites')}
        title={site.name}
        subtitle={`${siteConfig?.blueprintId || site.blueprintId || 'Site'}${site.createdAt ? ` • Created ${getServiceAge(new Date(site.createdAt))}` : ''}`}
        icon={
          <div className="w-full h-full bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center text-white">
            <GlobalOutlined className="text-lg" />
          </div>
        }
        actions={
          <>
            {siteUrl && siteStatus === 'active' && (
              <HeaderActionButton variant="primary" icon={<ExportOutlined />} onClick={() => window.open(siteUrl, '_blank')}>Visit Site</HeaderActionButton>
            )}
            {siteStatus === 'inactive' && (
              <HeaderActionButton variant="success" icon={<PlayCircleOutlined />} onClick={handleStart} disabled={isProcessing}>Start</HeaderActionButton>
            )}
            {siteStatus === 'active' && (
              <>
                <HeaderActionButton variant="secondary" icon={<PauseCircleOutlined />} onClick={() => setShowStopModal(true)} disabled={isProcessing}>Stop</HeaderActionButton>
                <HeaderActionButton variant="secondary" icon={<SyncOutlined />} onClick={handleRestart} disabled={isProcessing}>Restart Site</HeaderActionButton>
              </>
            )}
            <HeaderActionMenu items={[
              { key: 'refresh', label: 'Refresh', icon: <ReloadOutlined />, onClick: () => handleRefresh() },
              { key: 'delete', label: 'Delete Site', icon: <DeleteOutlined />, onClick: () => setShowDeleteModal(true), danger: true, disabled: isProcessing },
            ]} />
          </>
        }
        statusBanners={
          siteStatus === 'error' ? (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-r-lg">
              <div className="flex items-center gap-3">
                <CloseCircleOutlined className="text-red-500 text-xl" />
                <div>
                  <p className="text-sm font-semibold text-red-900 dark:text-red-300">Site Error</p>
                  <p className="text-sm text-red-800 dark:text-red-200 mt-0.5">The site is in an error state. Check events or try restarting.</p>
                </div>
              </div>
            </div>
          ) : undefined
        }
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {/* Overview Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Overview</h2>
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 ${currentStatusConfig.color} rounded-full ${siteStatus === 'deploying' ? 'animate-pulse' : ''}`}></div>
                <span className={`text-sm font-medium ${currentStatusConfig.textColor}`}>
                  {currentStatusConfig.text}
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Service ID</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white truncate max-w-[200px]">
                      {site.id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(site.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title="Copy ID"
                    >
                      <CopyOutlined className="text-xs" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Blueprint</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {siteConfig?.blueprintId || site.blueprintId || 'N/A'}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Bundle / Plan</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {siteConfig?.bundleId || site.bundleId || 'N/A'}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Region</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {site.region || 'N/A'}
                  </p>
                </div>

                {siteConfig?.environment && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Environment</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {siteConfig.environment}
                    </p>
                  </div>
                )}

                {(site.staticIp || site.publicIp) && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Static IP</label>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">
                        {site.staticIp || site.publicIp}
                      </code>
                      <button
                        onClick={() => copyToClipboard(site.staticIp || site.publicIp || '')}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        <CopyOutlined className="text-xs" />
                      </button>
                    </div>
                  </div>
                )}

                {siteUrl && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Site URL</label>
                    <a
                      href={siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                    >
                      {siteUrl}
                      <ExportOutlined className="text-xs" />
                    </a>
                  </div>
                )}

                {site.domainName && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Domain</label>
                    <div className="flex items-center gap-2">
                      <LinkOutlined className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{site.domainName}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('events')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'events'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Events
                </button>
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'overview'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('monitoring')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'monitoring'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Monitoring
                </button>
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'events' && (
                <LazyServiceEventsList serviceId={id || ''} eventsPerPage={10} />
              )}

              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {siteConfig?.description && (
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Description</label>
                      <p className="text-sm text-gray-900 dark:text-white">{siteConfig.description}</p>
                    </div>
                  )}
                  {site.product && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Instance Specifications</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {site.product.cpuCount && (
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">CPU</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{site.product.cpuCount} vCPU</div>
                          </div>
                        )}
                        {site.product.ramSizeInGb && (
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Memory</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{site.product.ramSizeInGb} GB</div>
                          </div>
                        )}
                        {site.product.diskSizeInGb && (
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Storage</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{site.product.diskSizeInGb} GB</div>
                          </div>
                        )}
                        {site.product.transferPerMonthInGb && (
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Transfer</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">{site.product.transferPerMonthInGb} GB</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'monitoring' && (
                <MonitoringTab service={site as any} />
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Status</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 ${currentStatusConfig.color} rounded-full`}></div>
                  <span className={`text-sm font-medium ${currentStatusConfig.textColor}`}>
                    {currentStatusConfig.text}
                  </span>
                </div>
              </div>

              {site.updatedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Last Updated</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {getServiceAge(new Date(site.updatedAt))}
                  </span>
                </div>
              )}

              {site.createdAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Created</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {getServiceAge(new Date(site.createdAt))}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Configuration Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Configuration</h3>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Blueprint</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                  {siteConfig?.blueprintId || site.blueprintId || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Bundle</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {siteConfig?.bundleId || site.bundleId || 'N/A'}
                </span>
              </div>
              {site.region && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Region</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {site.region}
                  </span>
                </div>
              )}
              {site.product?.price !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Price</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    ${site.product.price}/mo
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          {siteUrl && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Quick Links</h3>
              <div className="space-y-2">
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                >
                  <span>Visit Site</span>
                  <ExportOutlined />
                </a>
                {(siteConfig?.blueprintId || site.blueprintId || '').toLowerCase().includes('wordpress') && siteUrl && (
                  <a
                    href={`${siteUrl}/wp-admin`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                  >
                    <span>WordPress Admin</span>
                    <ExportOutlined />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Documentación */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Documentación</h3>
            <a
              href="https://docs.kubiy.com/static-sites"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <span>Ver documentación</span>
              <ExportOutlined className="text-xs" />
            </a>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Site"
        message={`Are you sure you want to delete "${site.name}"? This action cannot be undone and all data will be lost.`}
        confirmText="Delete"
        variant="danger"
        requireInput
        expectedInput={site.name}
        loading={isDeleting}
      />

      {/* Stop Modal */}
      <ConfirmationModal
        isOpen={showStopModal}
        onClose={() => setShowStopModal(false)}
        onConfirm={handleStop}
        title="Stop Site"
        message={`Are you sure you want to stop "${site.name}"? The site will no longer be accessible.`}
        confirmText="Stop"
        loading={isProcessing}
      />
    </div>
  );
};

export default SiteDetail;
